import React from 'react';
import { message } from 'antd';
import { useAtomValue, useSetAtom } from 'jotai';
import * as atoms from '../atom';
import { useResetState } from '../hooks';
import { gerResponseErrorMessage } from '../../utils';
import { FilterBuilderPanel, type FilterBuilderPanelProps } from './FilterBuilderPanel';

export const FilterPanel = () => {
  const dataSource = useAtomValue(atoms.dataSource);
  const callbacks = useAtomValue(atoms.callbacks);
  const filterCreate = useAtomValue(atoms.filterCreate);
  const filterEditor = useAtomValue(atoms.filterEditor);
  const filterClone = useAtomValue(atoms.filterClone);
  const setAsyncLoadingMask = useSetAtom(atoms.asyncLoadingMask);
  const { resetFilterClone, resetFilterCreate, resetFilterEditor } = useResetState();

  const handleCreateFilter: FilterBuilderPanelProps['onSave'] = async (filter) => {
    setAsyncLoadingMask(true);
    try {
      await callbacks.onCreateFilter?.(filter, dataSource);
      message.success(`${filter.name} created successfully!`);
      resetFilterCreate();
    } catch (e: any) {
      const errMsg = gerResponseErrorMessage(e) || `Failed to create ${filter.name}`;
      message.error(errMsg);
    }
    setAsyncLoadingMask(false);
  };

  const handleEditFilter: FilterBuilderPanelProps['onSave'] = async (filter, id) => {
    setAsyncLoadingMask(true);
    try {
      await callbacks.onEditFilter?.(id as number, filter, dataSource);
      resetFilterEditor();
      callbacks.onFilterEditorReset?.();
      message.success(`${filter.name} updated successfully!`);
    } catch (e: any) {
      const errMsg = gerResponseErrorMessage(e) || `Failed to update ${filter.name}`;
      message.error(errMsg);
    }
    setAsyncLoadingMask(false);
  };

  const handleCloneFilter: FilterBuilderPanelProps['onSave'] = async (filter) => {
    setAsyncLoadingMask(true);
    try {
      await callbacks.onCreateFilter?.(filter, dataSource);
      resetFilterClone();
      message.success(`${filter.name} created successfully!`);
    } catch (e: any) {
      const errMsg = gerResponseErrorMessage(e) || `Failed to create ${filter.name}`;
      message.error(errMsg);
    }
    setAsyncLoadingMask(false);
  };

  if (filterCreate) {
    return (
      <FilterBuilderPanel
        saveButtonText="Create"
        onSave={handleCreateFilter}
        onCancel={resetFilterCreate}
      />
    );
  }

  if (filterEditor) {
    return (
      <FilterBuilderPanel
        saveButtonText="Save"
        id={filterEditor.id}
        filter={filterEditor.filter}
        isPolicy={filterEditor.isPolicy}
        onSave={handleEditFilter}
        onCancel={() => {
          resetFilterEditor();
          callbacks.onFilterEditorReset?.();
        }}
      />
    );
  }

  if (filterClone) {
    return (
      <FilterBuilderPanel
        saveButtonText="Save"
        filter={filterClone.filter}
        onSave={handleCloneFilter}
        onCancel={resetFilterClone}
      />
    );
  }

  return null;
};
